<template>
  <div class="home-section-header">
    <div class="home-section-header__main">
      <div class="home-section-header__icon" aria-hidden="true">
        <i :class="icon"></i>
      </div>
      <h4 class="home-section-header__title" :title="title">{{ title }}</h4>
    </div>

    <div class="home-section-header__actions">
      <router-link
        :to="to"
        class="home-section-header__see-all home-section-header__see-all--desktop"
      >
        <i class="uil-list-ui-alt" aria-hidden="true"></i>
        {{ $t("home.see_all") }}
      </router-link>
      <router-link
        :to="to"
        class="home-section-header__see-all home-section-header__see-all--mobile"
      >
        {{ $t("general.all") }}
        <i class="uil-angle-right-b" aria-hidden="true"></i>
      </router-link>

      <template v-if="showSliderNavigation">
        <a
          class="home-section-header__slider-button slide-nav-prev"
          href="#"
          aria-label="Previous"
          uk-slider-item="previous"
        >
          <i class="uil-angle-left-b" aria-hidden="true"></i>
        </a>
        <a
          class="home-section-header__slider-button slide-nav-next"
          href="#"
          aria-label="Next"
          uk-slider-item="next"
        >
          <i class="uil-angle-right-b" aria-hidden="true"></i>
        </a>
      </template>
    </div>
  </div>
</template>

<script>
export default {
  name: "HomeSectionHeader",
  props: {
    title: {
      type: String,
      default: "",
    },
    icon: {
      type: String,
      required: true,
    },
    to: {
      type: [Object, String],
      required: true,
    },
    showSliderNavigation: {
      type: Boolean,
      default: false,
    },
  },
};
</script>

<style scoped>
.home-section-header {
  box-sizing: border-box;
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 12px;
  width: 100%;
  min-width: 0;
  min-height: 40px;
  margin-bottom: 8px;
}

.home-section-header__main {
  display: flex;
  align-items: center;
  flex: 1 1 auto;
  gap: 12px;
  min-width: 0;
  overflow: hidden;
}

.home-section-header__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  border-radius: 10px;
}

.home-section-header__icon i {
  color: var(--primaryColor);
  font-size: 20px;
}

.home-section-header__title {
  flex: 1 1 auto;
  min-width: 0;
  margin: 0 !important;
  overflow: hidden;
  color: #333;
  font-family: var(--font-secondary);
  font-size: var(--text-lg);
  font-weight: 600;
  line-height: 1.4;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.home-section-header__actions {
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
  gap: 5px;
  min-width: 0;
  white-space: nowrap;
}

.home-section-header__see-all {
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 40px;
  padding: 8px 16px;
  border-radius: 8px;
  color: #333;
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  font-weight: 500;
  opacity: 0.7;
  text-decoration: none;
  touch-action: manipulation;
  transition: color 0.2s ease, opacity 0.2s ease;
  -webkit-tap-highlight-color: transparent;
}

.home-section-header__see-all i {
  color: inherit;
  font-size: 14px;
}

.home-section-header__see-all--mobile {
  display: none;
}

.home-section-header__slider-button {
  position: relative;
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 32px;
  width: 32px;
  min-width: 32px;
  height: 32px;
  min-height: 32px;
  margin: 0;
  border-radius: 50%;
  background: #3e416d;
  color: #fff;
  text-decoration: none;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}

.home-section-header__slider-button i {
  color: inherit;
  font-size: 19px;
}

.home-section-header__slider-button.uk-invisible {
  visibility: visible !important;
  opacity: 0.15;
  pointer-events: none;
}

@media (hover: hover) {
  .home-section-header__see-all:hover {
    color: var(--iconColor);
    opacity: 0.8;
  }

  .home-section-header__slider-button:hover {
    background: #3e416d;
    color: #fff;
  }
}

@media (max-width: 959px) {
  .home-section-header {
    gap: 8px;
    min-height: 48px;
  }

  .home-section-header__main {
    gap: 8px;
  }

  .home-section-header__icon {
    border-radius: 8px;
  }

  .home-section-header__icon i {
    font-size: 18px;
  }

  .home-section-header__title {
    font-size: 20px;
    line-height: 1.4;
  }

  .home-section-header__see-all--desktop {
    display: none;
  }

  .home-section-header__see-all--mobile {
    display: inline-flex;
  }

  .home-section-header__see-all {
    min-width: 44px;
    min-height: 48px;
    padding: 8px 10px;
    font-size: 14px;
  }

  .home-section-header__see-all:active {
    opacity: 0.85;
  }

  .home-section-header__see-all i {
    font-size: 18px;
  }

  .home-section-header__slider-button {
    flex-basis: 48px;
    width: 48px;
    min-width: 48px;
    height: 48px;
    min-height: 48px;
  }
}

@media (max-width: 480px) {
  .home-section-header {
    gap: 4px;
  }

  .home-section-header__main {
    gap: 6px;
  }

  .home-section-header__icon {
    border-radius: 6px;
  }

  .home-section-header__icon i {
    font-size: 14px;
  }

  .home-section-header__title {
    font-size: 20px;
  }

  .home-section-header__actions {
    gap: 2px;
  }

  .home-section-header__see-all {
    padding-right: 6px;
    padding-left: 6px;
  }
}
</style>
